import React, { useState, useMemo } from 'react';
import { Badge } from '@/components/ui';
import { formatCurrency } from '@/utils/format';
import { ChevronRight, ChevronDown, Plus, Trash2, Edit2, Copy, Save, X, Hammer } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ConstructionBoqItem } from '../types/construction-boq.types';

interface BoqTreeTableProps {
  items: ConstructionBoqItem[];
  onEdit?: (item: ConstructionBoqItem) => void;
  onDelete?: (itemId: string) => void;
  onUpdateItem?: (itemId: string, updates: Partial<ConstructionBoqItem>) => Promise<void>;
}

// Helper to build tree
function buildTree(items: ConstructionBoqItem[]): ConstructionBoqItem[] {
  const map = new Map<string, ConstructionBoqItem & { children: any[] }>();
  
  items.forEach(item => {
    map.set(item.id, { ...item, children: [] });
  });

  const roots: any[] = [];

  items.forEach(item => {
    if (item.parent_id && map.has(item.parent_id)) {
      map.get(item.parent_id)!.children.push(map.get(item.id));
    } else {
      roots.push(map.get(item.id));
    }
  });

  // Sort by sort_order
  const sortTree = (nodes: any[]) => {
    nodes.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    nodes.forEach(n => sortTree(n.children));
  };
  sortTree(roots);

  return roots;
}

export function BoqTreeTable({ items, onEdit, onDelete, onUpdateItem }: BoqTreeTableProps) {
  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string>>(new Set());
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<{ quantity?: number; unit_price?: number }>({});
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();

  const treeData = useMemo(() => buildTree(items), [items]);

  const toggleExpand = (id: string) => {
    setExpandedRowKeys(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const allIds = items.filter(i => items.some(child => child.parent_id === i.id)).map(i => i.id);
    setExpandedRowKeys(new Set(allIds));
  };

  const collapseAll = () => {
    setExpandedRowKeys(new Set());
  };

  const handleEditClick = (node: any) => {
    setEditingId(node.id);
    setEditValues({
      quantity: node.quantity,
      unit_price: node.unit_price,
    });
  };

  const handleSave = async (node: any) => {
    if (!onUpdateItem) return;
    setIsSaving(true);
    try {
      const updates = {
        quantity: editValues.quantity,
        unit_price: editValues.unit_price,
        total_amount: (editValues.quantity || 0) * (editValues.unit_price || 0),
      };
      await onUpdateItem(node.id, updates);
      setEditingId(null);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  // Recursively render rows
  const renderRow = (node: any, depth: number) => {
    const isExpanded = expandedRowKeys.has(node.id);
    const hasChildren = node.children && node.children.length > 0;
    const isEditing = editingId === node.id;
    const isFolder = hasChildren || node.category_code === 'folder';

    return (
      <React.Fragment key={node.id}>
        <tr className={`border-b border-slate-200 hover:bg-slate-50 transition-colors ${isFolder ? 'bg-slate-50/50' : ''}`}>
          <td className="px-3 py-2">
            <div className="flex items-center gap-1" style={{ paddingLeft: `${depth * 20}px` }}>
              <div 
                className={`w-5 h-5 flex items-center justify-center rounded cursor-pointer hover:bg-slate-200 text-slate-500 ${!hasChildren ? 'invisible' : ''}`}
                onClick={() => toggleExpand(node.id)}
              >
                {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              </div>
              {isFolder ? (
                <span className="font-bold text-slate-800 text-sm">{node.item_code} - {node.item_name}</span>
              ) : (
                <div className="flex flex-col">
                  <span className="font-medium text-slate-800 text-sm">{node.item_name}</span>
                  <span className="text-xs text-slate-400 font-mono">{node.item_code}</span>
                </div>
              )}
            </div>
          </td>
          <td className="px-3 py-2 text-xs text-slate-600">
            {node.unit || '-'}
          </td>
          <td className="px-3 py-2 text-right">
            {isEditing && !isFolder ? (
              <input 
                type="number" 
                value={editValues.quantity || ''} 
                onChange={e => setEditValues(p => ({ ...p, quantity: Number(e.target.value) }))}
                className="w-20 text-right border border-slate-300 rounded px-2 py-1 text-sm focus:outline-emerald-500" 
              />
            ) : (
              <span className={`text-sm ${isFolder ? 'font-semibold text-slate-700' : 'text-slate-900'}`}>{node.quantity}</span>
            )}
          </td>
          <td className="px-3 py-2 text-right">
            {isEditing && !isFolder ? (
              <input 
                type="number" 
                value={editValues.unit_price || ''} 
                onChange={e => setEditValues(p => ({ ...p, unit_price: Number(e.target.value) }))}
                className="w-24 text-right border border-slate-300 rounded px-2 py-1 text-sm focus:outline-emerald-500" 
              />
            ) : (
              <span className="text-sm font-medium text-slate-700">{node.unit_price ? formatCurrency(node.unit_price) : '-'}</span>
            )}
          </td>
          <td className="px-3 py-2 text-right">
            <span className={`text-sm ${isFolder ? 'font-bold text-slate-900' : 'font-semibold text-emerald-600'}`}>
              {formatCurrency(isEditing ? (editValues.quantity || 0) * (editValues.unit_price || 0) : node.total_amount)}
            </span>
          </td>
          <td className="px-3 py-2 text-right">
            <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ opacity: 1 }}>
              {isEditing ? (
                <>
                  <button disabled={isSaving} onClick={() => setEditingId(null)} className="p-1.5 text-slate-400 hover:text-rose-500 hover:bg-rose-50 rounded">
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <button disabled={isSaving} onClick={() => handleSave(node)} className="p-1.5 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded">
                    <Save className="w-3.5 h-3.5" />
                  </button>
                </>
              ) : (
                <>
                  <button className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded">
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => handleEditClick(node)} className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded">
                    <Edit2 className="w-3.5 h-3.5" />
                  </button>
                  <button className="p-1.5 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded" title="Nhân bản">
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => navigate(`/work-orders/new?boq_item_id=${node.id}`)} className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded" title="Tạo Lệnh Thi Công (Work Order)">
                    <Hammer className="w-3.5 h-3.5" />
                  </button>
                  <button className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded" title="Xóa">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
          </td>
        </tr>
        {isExpanded && hasChildren && node.children.map((child: any) => renderRow(child, depth + 1))}
      </React.Fragment>
    );
  };

  return (
    <div className="flex flex-col bg-white w-full border-t border-slate-200">
      <div className="flex gap-2 p-2 bg-slate-50 border-b border-slate-200 text-xs">
        <button onClick={expandAll} className="px-3 py-1 text-slate-600 hover:bg-slate-200 rounded font-medium">Mở rộng tất cả</button>
        <button onClick={collapseAll} className="px-3 py-1 text-slate-600 hover:bg-slate-200 rounded font-medium">Thu gọn tất cả</button>
      </div>
      <div className="overflow-x-auto min-h-[400px]">
        <table className="w-full text-left border-collapse min-w-[800px]">
          <thead className="bg-slate-100 sticky top-0 z-10 border-b border-slate-200">
            <tr>
              <th className="px-3 py-2 text-xs font-semibold text-slate-700 w-auto">Hạng mục / Công tác</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-700 w-16">ĐVT</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-700 w-28 text-right">Khối lượng</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-700 w-36 text-right">Đơn giá (VNĐ)</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-700 w-40 text-right">Thành tiền (VNĐ)</th>
              <th className="px-3 py-2 text-xs font-semibold text-slate-700 w-32 text-right">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {treeData.length === 0 ? (
              <tr>
                <td colSpan={6} className="p-12 text-center text-slate-500">
                  Chưa có dữ liệu bóc tách BOQ.
                </td>
              </tr>
            ) : (
              treeData.map(node => renderRow(node, 0))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
